<template>
  <div 
    class="flow-object-file-group-outer"
    :class="[
      level !== 0 ? 'flow-object-file-group-nested': 'flow-object-file-group-root',
      `flow-object-file-group-level-${level}`,
    ]" 
    :style="getOuterStyles(level)">
    <!-- Group Header -->
    <div class="d-flex align-items-center justify-content-between" :class="{
      'mb-1': !isRoot,
      'mb-1': isRoot,
    }">
      <div class="d-flex">
        <span
          v-if="level === 0"
          class="font-weight-bold mb-0"
        >
          {{ group.label }}
        </span>
        <span
          v-else-if="level === 1"
          class="font-size-80 font-weight-500 mb-0"
        >
          {{ group.label }}
        </span>
        <span
          v-else
          class="font-size-80 font-weight-500 mb-0"
        >
          {{ group.label }}
        </span>
        <base-popover-icon
          v-if="group.description"
          :title="group.label"
          :content="group.description"
          icon="info-circle"
          class="ml-2"
        ></base-popover-icon>
      </div>
      <!-- <div v-if="!isRoot" class="bg-gray-150 flex-grow-1 mx-2" :style="{height: '0.1rem', borderRadius: '0.5rem'}"></div> -->
      <FileDownloadBundleButton
        v-if="allFilesIncludingChildren.length > 1"
        :category="allFilesIncludingChildren[0].category"
        :ids="allFilesIncludingChildren.map(f => f.id)"
        buttonSize="sm"
        buttonVariant="link"
        buttonText="Download All (.zip)"
        :tooltipText="`Download ${allFilesIncludingChildren.length} files as single zip file`"
        class="p-0"
        :style="{minWidth: '100px'}"
      ></FileDownloadBundleButton>
    </div>

    <hr class="mb-1 mt-1" />

    <!-- Files Section -->
    <div v-if="hasFiles">
      <base-s-3-file-download-button
        v-for="file in allFiles"
        :key="file.id"
        :category="file.category"
        :id="file.id"
        :file-data="getFileMeta(file)"
        hide-filesize
        show-popover-preview
        size="sm"
        class="w-100"
      />
    </div>

    <!-- Recursive Children -->
    <div v-if="hasChildren">
      <template v-for="(child, idx) in group.children">
        <flow-object-file-group
          :key="child.key"
          :group="child"
          :files-meta="filesMeta"
          :level="level + 1"
          :class="[level > 1 ? 'mt-2' : 'mt-3' , `index-${idx}`]"
        />  <!-- :class="{ 'mb-3': idx !== group.children.length - 1 }" -->
      </template>
    </div>

    <!-- No Files Message -->
    <div
      v-if="!hasFiles && !hasChildren"
      key="noFiles"
      class="text-center"
    >
      <p class="text-muted">No files to display</p>
    </div>
  </div>
</template>

<script>
  import FileDownloadBundleButton from '@/components/s3_files/FileDownloadBundleButton.vue';
  import { getS3FileMetaKey } from '@/components/s3_files/s3_file_meta';

  export default {
    name: 'FlowObjectFileGroup',
    components: {
      FileDownloadBundleButton,
    },
    props: {
      group: { type: Object, required: true },
      level: { type: Number, default: 0 },
      filesMeta: { type: Object, default: () => ({}) },  // file meta for ALL files, keyed by 'category:id' - from get_filemanager_data
    },
    computed: {
      allFiles() {
        // Flatten all files from fields in the group - this does NOT include files from children groups
        if (!this.group || !this.group.fields || !Array.isArray(this.group.fields)) {
          return [];
        }
        return this.group.fields.filter((f) => Array.isArray(f?.files)).flatMap((f) => f.files || []);
      },
      allFilesIncludingChildren() {
        // Flatten all files from fields in the group AND from children groups
        const allFiles = [];
        const collectFiles = (group) => {
          const groupFiles = group.fields ? group.fields.filter((f) => Array.isArray(f?.files)).flatMap((f) => f.files || []) : [];
          // console.log(`groupFileIds for ${group.label}:`, groupFiles.map(f => f.id));
          allFiles.push(...groupFiles);
          if (group.children && Array.isArray(group.children)) {
            group.children.forEach(child => collectFiles(child));
          }
        };
        collectFiles(this.group);
        // console.log('allFilesIncludingChildren', allFiles);
        return allFiles;
      },
      allFileIdsIncludingChildren() {
        // Get all file IDs from fields in the group AND from children groups
        const ids = this.allFilesIncludingChildren.map((f) => f.id);
        // console.log('allFileIdsIncludingChildren', ids);
        return ids;
      },
      hasChildren() {
        if (!this.group || !this.group.children || !Array.isArray(this.group.children)) {
          return false;
        }
        return this.group.children.length > 0;
      },
      hasFiles() {
        return this.allFiles.length > 0;
      },
      hasMultipleFiles() {
        return this.allFiles.length > 1;
      },
      allFileIds() {
        return this.allFiles.map((f) => f.id);
      },
      isNested() {
        return this.level > 0;
      },
      isRoot() {
        return this.level === 0;
      },
    },
    methods: {
      getFileMeta(file) {
        // if missing, the button falls back to fetching it (batched) from category/id
        return this.filesMeta?.[getS3FileMetaKey(file.category, file.id)];
      },
      getOuterStyles(level) {
        // each additional level of indentation is 1rem.  But we have 0.4rem border on the left, so the first level is 0.6rem
        if (level < 1) {
          return {};  // No indentation for level 0, no border
        }
        return {
          paddingLeft: '1rem',
        };
      },
    },
  };
</script>

<style lang="scss" scoped>
.flow-object-file-group-outer.flow-object-file-group-nested {
  position: relative;
  &:before {
    content: '';
    display: block;
    height: 100%;
    width: 0.4rem;
    background-color: var(--gray-150);
    border-radius: 0.2rem;
    position: absolute;
    left: 0;
    top: 0;
  }
  &.flow-object-file-group-level-1 {
    // we make the "before" element a bit darker for level 1
    &:before {
      background-color: var(--gray-200);
    }
  }
}
</style>